// ---------------------------------------------------------------------------
// The Schedule step's rules (R17b)
//
// The time zones offered, which channels the step adapts to, the send days,
// and the LinkedIn business-hours warning. Taken out of wizard/wiz-schedule.ts
// unchanged, so the step's rebuild (R17l) redoes only the markup. Plain
// TypeScript.
// ---------------------------------------------------------------------------

import type { ScheduleConfig, SequenceStep, Weekday } from '../types.js';

export const TIMEZONES = [
  { value: 'America/New_York', label: 'America / New York' },
  { value: 'America/Chicago', label: 'America / Chicago' },
  { value: 'America/Denver', label: 'America / Denver' },
  { value: 'America/Los_Angeles', label: 'America / Los Angeles' },
  { value: 'Europe/London', label: 'Europe / London' },
  { value: 'Europe/Berlin', label: 'Europe / Berlin, Paris' },
  { value: 'Europe/Helsinki', label: 'Europe / Helsinki, Athens' },
  { value: 'Europe/Istanbul', label: 'Europe / Istanbul' },
  { value: 'Asia/Dubai', label: 'Asia / Dubai' },
  { value: 'Asia/Singapore', label: 'Asia / Singapore' },
  { value: 'Asia/Tokyo', label: 'Asia / Tokyo' },
  { value: 'Australia/Sydney', label: 'Australia / Sydney' },
  { value: 'UTC', label: 'UTC' },
];

export type TimezoneOption = (typeof TIMEZONES)[number];

/** The zone shown as picked: the schedule's, or the first one when it names none of the list. */
export const selectedTimezone = (timezone: string): TimezoneOption =>
  TIMEZONES.find(tz => tz.value === timezone) ?? TIMEZONES[0]!;

/**
 * The step adapts itself to whichever channels the sequence uses: with
 * LinkedIn in it, the business-hours warning and the fixed daily-limit notice;
 * with only LinkedIn, no daily-limit input at all.
 */
export function scheduleChannels(sequence: SequenceStep[]): { hasLinkedin: boolean; linkedinOnly: boolean } {
  const hasLinkedin = sequence.some(s => s.channel === 'linkedin_connect' || s.channel === 'linkedin_dm');
  const hasEmail = sequence.some(s => s.channel === 'email');
  return { hasLinkedin, linkedinOnly: hasLinkedin && !hasEmail };
}

/** A send day ticked or unticked; the days stay in weekday order. */
export const withSendDayToggled = (schedule: ScheduleConfig, weekday: Weekday): ScheduleConfig => ({
  ...schedule,
  sendDays: schedule.sendDays.some(d => d.day === weekday.day)
    ? schedule.sendDays.filter(d => d.day !== weekday.day)
    : [...schedule.sendDays, weekday].sort((a, b) => a.day - b.day),
});

/** Whether a day is one of the send days. */
export const isSendDay = (schedule: ScheduleConfig, weekday: Weekday): boolean =>
  schedule.sendDays.some(d => d.day === weekday.day);

/**
 * Sending outside business hours (9–17) can get a LinkedIn sender blocked.
 * Only the hours are compared, as before (08:30 counts as before 9, 17:30 not
 * as after 17).
 */
export const outsideBusinessHours = (schedule: ScheduleConfig): boolean =>
  schedule.startTime.getHours() < 9 || schedule.endTime.getHours() > 17;

/** The daily limit a cleared input stands for. */
export const DEFAULT_DAILY_LIMIT = 30;
